import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import type { CSSProperties } from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { Gift } from '@sorare/core/src/atoms/icons/Gift';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BrandS, LabelM } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { glossary, navLabels } from '@sorare/core/src/lib/glossary';
import { isType, isTypes } from '@sorare/core/src/lib/gql';
import type { HexColor } from '@sorare/core/src/style/types.ts';

import { getTaskCompetitionColor } from '#src/lib/task/index.tsx';

import { RewardThresholdsGauge } from './RewardThresholdsGauge/index.tsx';
import { ThresholdRewardRow } from './ThresholdRewardRow/index.tsx';
import type { TaskRewards_taskInterface } from './__generated__/index.graphql.ts';

const StyledNeonEdge = styled(NeonEdge)`
  isolation: isolate;
  padding: calc(6 * var(--unit)) var(--double-unit) var(--double-unit);
  border-radius: var(--double-unit);
  border: 1px solid var(--competition-color);
  background:
    linear-gradient(180deg, rgba(14, 14, 14, 0) 0%, #0e0e0e 60%),
    conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, var(--c-black) 0)
      0 0/5px 5px;
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
`;

const SectionChevron = styled(ChevronDownBold)<{ $expanded?: boolean }>`
  transform: rotate(${({ $expanded }) => ($expanded ? 180 : 0)}deg);
  flex-shrink: 0;
`;

const ThresholdDetailsHeader = styled(ButtonBase)`
  align-items: center;
  display: flex;
  gap: var(--unit);
  z-index: 1;
`;

const ThresholdsList = styled(Vertical).attrs({ gap: 1 })`
  padding: var(--unit) 0;
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background-color: var(--c-nd-200);
`;

type Props = {
  task: TaskRewards_taskInterface;
};

export const TaskRewards = ({ task }: Props) => {
  const [expanded, setExpanded] = useState(false);
  const { formatMessage } = useIntl();

  if (
    !isTypes(task, [
      'CardsCountTargetTask',
      'CompleteCollectionsCountTargetTask',
    ])
  ) {
    return null;
  }

  const { parentTask } = task;
  if (!isType(parentTask, 'TasksTrack') || !parentTask.steps?.length) {
    return null;
  }

  const { steps } = parentTask;
  const isCompleteCollectionsCountTask = isType(
    task,
    'CompleteCollectionsCountTargetTask'
  );
  const taskSetColor = ((isType(task, 'CardsCountTargetTask')
    ? task.cardSet?.branding?.colors?.surfacePrimary
    : null) ?? 'var(--c-nd-600-opaque)') as HexColor;
  const competitionColor = getTaskCompetitionColor(task, taskSetColor);

  const firstNonClaimedIndex = steps.findIndex(
    s =>
      s.aasmState !== ManagerProgressionTaskState.CLAIMED &&
      s.aasmState !== ManagerProgressionTaskState.ASSIGNED
  );
  const effectiveIndex =
    firstNonClaimedIndex === -1 ? steps.length - 1 : firstNonClaimedIndex;
  const currentStep = steps[effectiveIndex];

  const currentStepProgress =
    isTypes(currentStep, [
      'CardsCountTargetTask',
      'CompleteCollectionsCountTargetTask',
    ]) && typeof currentStep.progress === 'number'
      ? currentStep.progress
      : 0;

  return (
    <Vertical
      style={{ '--competition-color': competitionColor } as CSSProperties}
    >
      <Horizontal>
        <Gift size={2} color={taskSetColor} />
        <BrandS>
          <FormattedMessage {...glossary.rewards} />
        </BrandS>
      </Horizontal>
      <BodyM>
        <FormattedMessage
          defaultMessage="Earn more reward as you complete more {item}!"
          values={{
            item: isCompleteCollectionsCountTask
              ? formatMessage(navLabels.collections).toLowerCase()
              : formatMessage(glossary.cards).toLowerCase(),
          }}
        />
      </BodyM>
      <StyledNeonEdge
        color={competitionColor}
        side="top"
        spread="50%"
        haloOpacity={0.5}
      >
        <RewardThresholdsGauge
          targets={steps.map(step => step.target)}
          effectiveIndex={effectiveIndex}
          liveScore={currentStepProgress}
          iconColor={taskSetColor}
        />
        <Divider />
        <Vertical>
          <ThresholdDetailsHeader onClick={() => setExpanded(prev => !prev)}>
            <SectionChevron $expanded={expanded} />
            <LabelM bold>
              <FormattedMessage {...glossary.rewards} />
            </LabelM>
          </ThresholdDetailsHeader>

          <Collapse
            open={expanded}
            gap={0}
            collapsedContent={
              <ThresholdRewardRow
                step={currentStep}
                index={effectiveIndex}
                currentThresholdIndex={effectiveIndex}
                competitionColor={competitionColor}
                isCompleteCollectionsCountTask={isCompleteCollectionsCountTask}
              />
            }
          >
            <ThresholdsList>
              {steps.map((step, i) => (
                <ThresholdRewardRow
                  key={step.id}
                  step={step}
                  index={i}
                  currentThresholdIndex={effectiveIndex}
                  competitionColor={competitionColor}
                  isCompleteCollectionsCountTask={
                    isCompleteCollectionsCountTask
                  }
                />
              ))}
            </ThresholdsList>
          </Collapse>
        </Vertical>
      </StyledNeonEdge>
    </Vertical>
  );
};

TaskRewards.fragments = {
  taskInterface: gql`
    fragment TaskRewards_taskInterface on TaskInterface {
      id
      ... on CardsCountTargetTask {
        id
        cardSet {
          slug
          branding {
            id
            colors {
              surfacePrimary
            }
          }
        }
        parentTask {
          id
          ... on TasksTrack {
            id
            steps {
              id
              aasmState
              ... on CardsCountTargetTask {
                id
                progress
                target
              }
              ... on CompleteCollectionsCountTargetTask {
                id
                progress
                target
              }
              ...ThresholdRewardRow_taskInterface
            }
          }
        }
      }
      ... on CompleteCollectionsCountTargetTask {
        id
        parentTask {
          id
          ... on TasksTrack {
            id
            steps {
              id
              aasmState
              ... on CardsCountTargetTask {
                id
                progress
                target
              }
              ... on CompleteCollectionsCountTargetTask {
                id
                progress
                target
              }
              ...ThresholdRewardRow_taskInterface
            }
          }
        }
      }
      ...getTaskCompetitionColor_taskInterface
    }
    ${ThresholdRewardRow.fragments.taskInterface}
    ${getTaskCompetitionColor.fragments.taskInterface}
  ` as TypedDocumentNode<TaskRewards_taskInterface>,
};
